import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Text,
    Button,
    themeVars,
    AlertInline,
    ChevronDownIcon,
    ChevronUpIcon
} from '@eds-web/react';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';

import { StyledPrizeContainer } from './Prizes.style';
import { StepComponentProps } from './PrizeStep';

import { device } from '../../constants/breakpoint';
import { useFirstPrizeWithInterviewUrl } from '../../hooks/useFirstPrizeWithInterviewUrl';
import { selectAccountIsUnder18 } from '../../selectors/account.selector';

interface Step1ComponentProps extends StepComponentProps {
    handleButtonClick: any;
}

const AdditionalContent = () => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <>
            {!isExpanded ? (
                <Text
                    variant="paragraphMD"
                    style={{
                        cursor: 'pointer',
                        color: themeVars.color.transparent.light['100'],
                        display: 'inline-block',
                        marginTop: '8px'
                    }}
                    onClick={handleToggle}
                >
                    {t('epic.esport.step1.example.showMore')}
                    <ChevronDownIcon />
                </Text>
            ) : (
                <Text variant="paragraphMD">
                    <ul>
                        <li
                            dangerouslySetInnerHTML={{
                                __html: t('epic.esport.step1.example.more.example1')
                            }}
                        />
                        <li>{t('epic.esport.step1.example.more.example2')}</li>
                        <Text className={'example-content'} variant="paragraphMD">
                            <ul>
                                <li>{t('epic.esport.step1.example.more.example2.list1')}</li>
                                <li>{t('epic.esport.step1.example.more.example2.list2')}</li>
                                <li>{t('epic.esport.step1.example.more.example2.list3')}</li>
                                <li>{t('epic.esport.step1.example.more.example2.list4')}</li>
                                <li>{t('epic.esport.step1.example.more.example2.list5')}</li>
                                <li>{t('epic.esport.step1.example.more.example2.list6')}</li>
                            </ul>
                        </Text>
                    </ul>
                    <Text
                        variant="paragraphMD"
                        style={{
                            cursor: 'pointer',
                            color: themeVars.color.transparent.light['100'],
                            display: 'inline-block',
                            marginTop: '8px'
                        }}
                        onClick={handleToggle}
                    >
                        {t('epic.esport.step1.example.hide')}
                        <ChevronUpIcon />
                    </Text>
                </Text>
            )}
        </>
    );
};

const InterviewUnder18Content = () => {
    const { t } = useTranslation();
    return (
        <StyledPrizeContainer>
            <Text variant="uiMDBold">{t('epic.esport.step1.interview.title1')}</Text>
            <Text variant="paragraphMD">{t('epic.esport.step1.interview.content1')}</Text>
            <Text variant="uiMDBold">{t('epic.esport.step1.interview.under18.title2')}</Text>
            <Text variant="paragraphMD">
                {t('epic.esport.step1.interview.under18.content2.desc1')}
            </Text>
            <Text variant="paragraphMD">
                {t('epic.esport.step1.interview.under18.content2.desc2')}
            </Text>
            <Text variant="uiMDBold">{t('epic.esport.step1.example.title')}</Text>
            <Text variant="paragraphMD">
                <ul>
                    <li>{t('epic.esport.step1.example.list1')}</li>
                    <li>{t('epic.esport.step1.example.list2')}</li>
                    <li>{t('epic.esport.step1.example.list3')}</li>
                    <li>{t('epic.esport.step1.example.list4')}</li>
                    <AdditionalContent />
                </ul>
            </Text>
        </StyledPrizeContainer>
    );
};

const InterviewAdultContent = () => {
    const { t } = useTranslation();
    return (
        <>
            <Text variant="uiMDBold">{t('epic.esport.step1.interview.title1')}</Text>
            <Text variant="paragraphMD">{t('epic.esport.step1.interview.content1')}</Text>
            <Text variant="uiMDBold">{t('epic.esport.step1.example.title')}</Text>
            <Text variant="paragraphMD">
                <ul>
                    <li>{t('epic.esport.step1.example.list1')}</li>
                    <li>{t('epic.esport.step1.example.list2')}</li>
                    <li>{t('epic.esport.step1.example.list3')}</li>
                    <li>{t('epic.esport.step1.example.list4')}</li>
                    <AdditionalContent />
                </ul>
            </Text>
        </>
    );
};

const MobileButtonContainer = styled.div`
    margin-top: 16px;
    @media screen and ${device.laptop} {
        display: flex;
        justify-content: center;

        button {
            width: 100%;
        }
    }
`;

const ContentContainer = styled.div`
    gap: 16px;
    display: flex;
    flex-direction: column;
    padding-top: 32px;
`;

const Interview: React.FC<Step1ComponentProps> = (props) => {
    const { t } = useTranslation();
    const { isCompleted = false } = props;

    const { showGate, isUnder18 } = useFirstPrizeWithInterviewUrl();

    const renderContent = () => {
        if (isUnder18) {
            return <InterviewUnder18Content />;
        }
        return <InterviewAdultContent />;
    };

    return (
        <>
            <StyledPrizeContainer>
                <ContentContainer>
                    <Text variant="headingMD">{t('epic.esport.step1.title')}</Text>
                    {renderContent()}
                    <AlertInline
                        style={{ marginTop: 24 }}
                        status={'warning'}
                        message={t('epic.esport.interview.warning1')}
                    />
                    {isCompleted && (
                        <article>
                            <Text variant="uiMDBold">{t('epic.esport.step1.done.title')}</Text>
                            <Text variant="paragraphMD">{t('epic.esport.step1.done.content')}</Text>
                        </article>
                    )}
                </ContentContainer>
            </StyledPrizeContainer>
            {!isCompleted && props.winning?.taxInterviewUrl && (
                <MobileButtonContainer>
                    <Button onClick={() => props.handleButtonClick(showGate)} size="lg">
                        {t('epic.esport.actions.startTaxInterview')}
                    </Button>
                </MobileButtonContainer>
            )}
        </>
    );
};

const ReleaseUnder18Content: React.FC<Step1ComponentProps> = (props) => {
    const { t } = useTranslation();
    return (
        <>
            <Text variant="paragraphMD">{t('epic.esport.step1.release.under18.content1')}</Text>
            {!props?.isCash || props.offlinePayment ? (
                <Text variant="paragraphMD">
                    {t('epic.esport.step1.release.under18.content2.offline')}
                </Text>
            ) : (
                <Text variant="paragraphMD">
                    {t('epic.esport.step1.release.under18.content2.hw')}
                </Text>
            )}
        </>
    );
};

const ReleaseAdultContent: React.FC = () => {
    const { t } = useTranslation();
    return (
        <>
            <Text variant="paragraphMD">{t('epic.esport.step1.release.adult.content1')}</Text>
        </>
    );
};

const Release: React.FC<Step1ComponentProps> = (props) => {
    const { t } = useTranslation();
    const { isCompleted = false } = props;
    const isUnder18 = useSelector(selectAccountIsUnder18);
    const renderContent = () => {
        if (isUnder18) {
            return <ReleaseUnder18Content {...props} />;
        }
        return <ReleaseAdultContent />;
    };

    return (
        <>
            <StyledPrizeContainer>
                <ContentContainer>
                    <Text variant="headingMD">{t('epic.esport.step1.release.title')}</Text>
                    <article>
                        {renderContent()}
                        <AlertInline
                            style={{ marginTop: 24 }}
                            status={'warning'}
                            message={t('epic.esport.step1.release.alert')}
                        />
                    </article>
                </ContentContainer>
            </StyledPrizeContainer>
            {!isCompleted && (
                <MobileButtonContainer>
                    <Button onClick={() => props.handleButtonClick(false)} size="lg">
                        {t('epic.esport.actions.signReleaseForm')}
                    </Button>
                </MobileButtonContainer>
            )}
        </>
    );
};

export interface Step1Props extends StepComponentProps {
    onButtonClick?: () => void;
}

export const Step1: React.FC<Step1Props> = (props) => {
    const { identityTaxVerified, onButtonClick } = props;

    return (
        <>
            {identityTaxVerified ? (
                <Release {...props} handleButtonClick={onButtonClick} />
            ) : (
                <Interview {...props} handleButtonClick={onButtonClick} />
            )}
        </>
    );
};
